import React from 'react';
import { Typography } from '@mui/material';
import LegalPageLayout from './LegalPageLayout';
import { useTenantConfig } from '../../contexts/TenantConfigContext';

const TermsOfServicePage: React.FC = () => {
  const { config } = useTenantConfig();
  const brandName = config?.branding?.displayName || 'AI-SCRIBE';
  const contactEmail = config?.accountManager?.email || 'support@ai-scribe.net';

  return (
    <LegalPageLayout title="Terms of Service" lastUpdated="17 March 2026">
      <Typography component="h2">1. Introduction</Typography>
      <Typography component="p">
        Welcome to {brandName} (&quot;we&quot;, &quot;us&quot;, or &quot;our&quot;). These Terms of
        Service (&quot;Terms&quot;) govern your access to and use of the {brandName} platform,
        including our website, web application, APIs, and related services (collectively, the
        &quot;Service&quot;).
      </Typography>
      <Typography component="p">
        By creating an account or using the Service, you agree to be bound by these Terms. If you do
        not agree, do not use the Service.
      </Typography>

      <Typography component="h2">2. Eligibility</Typography>
      <Typography component="p">
        You must be at least 18 years old and have the legal capacity to enter into a binding
        agreement to use the Service. By using {brandName}, you represent and warrant that you meet
        these requirements.
      </Typography>

      <Typography component="h2">3. Account Registration</Typography>
      <Typography component="p">
        To access the Service, you must create an account with accurate and complete information.
        You are responsible for maintaining the confidentiality of your login credentials and for all
        activity that occurs under your account. You must notify us immediately of any unauthorized
        use.
      </Typography>

      <Typography component="h2">4. Subscriptions and Payments</Typography>
      <Typography component="p">
        {brandName} offers paid subscription plans billed on a monthly basis. Payment processing is
        handled by Paddle.com Market Ltd (&quot;Paddle&quot;), our Merchant of Record. By
        subscribing, you also agree to Paddle&apos;s{' '}
        <a href="https://www.paddle.com/legal/terms" target="_blank" rel="noopener noreferrer">
          Terms of Use
        </a>{' '}
        and{' '}
        <a href="https://www.paddle.com/legal/privacy" target="_blank" rel="noopener noreferrer">
          Privacy Policy
        </a>
        .
      </Typography>
      <Typography component="p">
        Subscriptions automatically renew at the end of each billing period unless cancelled before
        the renewal date. You may cancel your subscription at any time from your account settings;
        the cancellation will take effect at the end of the current billing period, and you will
        retain access until then.
      </Typography>
      <Typography component="p">
        We reserve the right to change pricing with at least 30 days&apos; notice. Existing
        subscribers will be notified before any price change takes effect.
      </Typography>

      <Typography component="h2">5. Use of the Service</Typography>
      <Typography component="p">
        The Service provides AI-powered audio transcription, document extraction, text
        transformation, and related tools. You may use the Service only for lawful purposes and in
        accordance with these Terms.
      </Typography>
      <Typography component="p">You agree not to:</Typography>
      <ul>
        <li>Use the Service for any illegal or unauthorized purpose;</li>
        <li>Upload content that infringes third-party intellectual property rights;</li>
        <li>Attempt to reverse-engineer, decompile, or disassemble any part of the Service;</li>
        <li>Use automated systems (bots, scrapers) to access the Service without our written consent;</li>
        <li>Transmit viruses, malware, or any harmful code;</li>
        <li>Interfere with or disrupt the Service or its infrastructure.</li>
      </ul>

      <Typography component="h2">6. Your Content</Typography>
      <Typography component="p">
        You retain ownership of all content you upload to the Service (&quot;Your Content&quot;). By
        uploading content, you grant us a limited, non-exclusive licence to process, store, and
        display Your Content solely to provide the Service to you.
      </Typography>
      <Typography component="p">
        We do not use Your Content to train AI models. Uploaded files and generated outputs are
        stored temporarily for the purpose of delivering the Service and are automatically deleted
        in accordance with our data retention policy.
      </Typography>

      <Typography component="h2">7. Intellectual Property</Typography>
      <Typography component="p">
        The Service, including its design, code, features, and branding, is owned by us and
        protected by applicable intellectual property laws. Nothing in these Terms grants you any
        right to use our trademarks, logos, or brand elements without prior written consent.
      </Typography>

      <Typography component="h2">8. Disclaimer of Warranties</Typography>
      <Typography component="p">
        The Service is provided &quot;as is&quot; and &quot;as available&quot; without warranties of
        any kind, whether express or implied, including but not limited to warranties of
        merchantability, fitness for a particular purpose, or non-infringement. We do not guarantee
        that the Service will be uninterrupted, error-free, or completely accurate.
      </Typography>
      <Typography component="p">
        AI-generated outputs are provided for informational and productivity purposes only. They
        should not be relied upon as legal, medical, financial, or professional advice. You are
        solely responsible for reviewing and verifying any output before use.
      </Typography>

      <Typography component="h2">9. Limitation of Liability</Typography>
      <Typography component="p">
        To the maximum extent permitted by applicable law, we shall not be liable for any indirect,
        incidental, special, consequential, or punitive damages, or any loss of profits, data, or
        goodwill, arising out of or in connection with your use of the Service. Our total aggregate
        liability shall not exceed the amount you paid to us in the twelve (12) months preceding
        the claim.
      </Typography>

      <Typography component="h2">10. Termination</Typography>
      <Typography component="p">
        We may suspend or terminate your account at any time if you breach these Terms or engage in
        conduct we reasonably consider harmful to other users or the Service. You may delete your
        account at any time by contacting us at{' '}
        <a href={`mailto:${contactEmail}`}>{contactEmail}</a>.
      </Typography>
      <Typography component="p">
        Upon termination, your right to use the Service ceases immediately. Sections that by their
        nature should survive termination (including limitations of liability, disclaimers, and
        governing law) will continue to apply.
      </Typography>

      <Typography component="h2">11. Changes to These Terms</Typography>
      <Typography component="p">
        We may update these Terms from time to time. If we make material changes, we will notify
        you via email or through the Service at least 14 days before the changes take effect. Your
        continued use of the Service after the effective date constitutes acceptance of the updated
        Terms.
      </Typography>

      <Typography component="h2">12. Governing Law</Typography>
      <Typography component="p">
        These Terms are governed by and construed in accordance with the laws of the Republic of
        South Africa. Any disputes arising under these Terms shall be subject to the exclusive
        jurisdiction of the courts of South Africa.
      </Typography>

      <Typography component="h2">13. Contact</Typography>
      <Typography component="p">
        If you have questions or concerns about these Terms, please contact us at{' '}
        <a href={`mailto:${contactEmail}`}>{contactEmail}</a>.
      </Typography>
    </LegalPageLayout>
  );
};

export default TermsOfServicePage;
